import React from 'react';
import { useSelector } from 'react-redux';
import { UserProfileDetailsSelector } from '@store/selectors/UserProfileSelector';
import { Hospital } from '@interfaces/Hospital';

interface UserProfileForPendo {
	username: string;
	email: string;
	firstName: string;
	lastName: string;
	userRoles?: string[];
	primaryPractice?: { id: string; name: string };
	primaryHospital?: Hospital & { ministry?: unknown };
}

function isPrimitive(v: unknown): v is string | boolean | number {
	return !!(v && ['string', 'boolean', 'number'].includes(typeof v));
}

export function buildPendoVisitor(userProfile: UserProfileForPendo): pendo.IdentityMetadata {
	const { username, email, firstName, lastName, userRoles = [], primaryHospital, primaryPractice } = userProfile;

	const visitor: pendo.IdentityMetadata = {
		id: username.split('\\')[1] ?? username,
		email,
		fullName: `${lastName}, ${firstName}`,
		role: [...userRoles].sort().join(','),
	};

	if (primaryPractice) {
		visitor.practiceId = primaryPractice.id;
		visitor.practiceName = primaryPractice.name;
	}

	if (primaryHospital) {
		visitor.hospitalId = primaryHospital.id;
		visitor.hospitalName = primaryHospital.name;
		if (isPrimitive(primaryHospital.ministry)) {
			visitor.ministry = primaryHospital.ministry;
		}
	}

	return visitor;
}

const usePendoInitialization = () => {
	const userInfo = useSelector(UserProfileDetailsSelector);
	const userInfoRef = React.useRef(userInfo);

	React.useEffect(() => {
		userInfoRef.current = userInfo;
	}, [userInfo]);

	const [shouldInitialize, setShouldInitialize] = React.useState(false);

	React.useEffect(() => {
		if (!userInfoRef.current || !Object.keys(userInfoRef.current).length) { return; }
		setShouldInitialize(true);
	}, [userInfo]);

	React.useEffect(() => {
		if (!shouldInitialize) { return; }

		const {
			username,
			email,
			firstName,
			lastName,
			userRoles = [],
			primaryHospital,
			primaryPractice,
		} = userInfoRef.current;

		const visitorDescriptor = buildPendoVisitor({
			username, email, firstName, lastName, userRoles, primaryHospital, primaryPractice,
		});

		const initOptions: pendo.InitOptions & { frameIdentitySync: boolean } = {
			visitor: visitorDescriptor,
			frameIdentitySync: true,
		};
		pendo.initialize(initOptions);
	}, [shouldInitialize]);
};

export default usePendoInitialization;
